import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, XCircle, Clock, FileDown, Plus, X, Landmark } from "lucide-react";
import { API, inputCls, btnPrimary, btnGhost, Field } from "./crm/shared";

const BANKS = { BG: "Banco General", Banesco: "Banesco", Banistmo: "Banistmo" };
const money = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS = {
  pendiente: { label: "Pendiente", cls: "bg-amber-100 text-amber-800", Icon: Clock },
  aprobado: { label: "Aprobado", cls: "bg-[#0fe814]/20 text-[#0a7d0d]", Icon: CheckCircle2 },
  rechazado: { label: "Rechazado", cls: "bg-[#ff603e]/15 text-[#c2410c]", Icon: XCircle },
};

export const CashoutBadge = ({ status }) => {
  const m = STATUS[status] || STATUS.pendiente;
  return <span data-testid={`cashout-status-${status}`} className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full ${m.cls}`}><m.Icon size={12} /> {m.label}</span>;
};

export const downloadReceipt = async (url, headers, name) => {
  const res = await axios.get(url, { headers, responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(res.data);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

export const EMPTY_CASHOUT = { store: "", client_id: "", phone: "", date: new Date().toISOString().slice(0, 10), address: "", amount: "", beneficiary: "", bank: "BG", account: "", account_type: "ahorro", email: "", trx_id: "" };

export const CashoutForm = ({ initial = EMPTY_CASHOUT, onSubmit, onCancel, submitting, error, testPrefix = "co" }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const fields = [
    ["store", "Tienda"], ["client_id", "ID Cliente"], ["phone", "Teléfono"], ["date", "Fecha", "date"], ["address", "Dirección"], ["amount", "Monto (USD)", "number"],
    ["beneficiary", "Beneficiario"], ["account", "Cuenta"], ["email", "Correo", "email"], ["trx_id", "ID de TRX"],
  ];
  return (
    <form data-testid={`${testPrefix}-form`} onSubmit={(e) => { e.preventDefault(); onSubmit({ ...f, amount: Number(f.amount) }); }} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {fields.map(([k, label, type]) => (
        <Field key={k} label={label}>
          <input data-testid={`${testPrefix}-${k}`} type={type || "text"} step={type === "number" ? "0.01" : undefined} min={type === "number" ? "0.01" : undefined} required className={inputCls} value={f[k]} onChange={set(k)} />
        </Field>
      ))}
      <Field label="Banco">
        <select data-testid={`${testPrefix}-bank`} className={inputCls} value={f.bank} onChange={set("bank")}>
          {Object.entries(BANKS).map(([k, v]) => <option key={k} value={k}>{v} ({k})</option>)}
        </select>
      </Field>
      <Field label="Tipo de Cuenta">
        <select data-testid={`${testPrefix}-account_type`} className={inputCls} value={f.account_type} onChange={set("account_type")}>
          <option value="ahorro">Cuenta de Ahorro</option>
          <option value="corriente">Cuenta Corriente</option>
        </select>
      </Field>
      <div className="sm:col-span-2 lg:col-span-3 flex flex-wrap items-center gap-2">
        <button data-testid={`${testPrefix}-submit`} type="submit" disabled={submitting} className={btnPrimary}>{submitting ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Enviar solicitud</button>
        {onCancel && <button type="button" onClick={onCancel} className={btnGhost}><X size={13} /> Cancelar</button>}
        {error && <span data-testid={`${testPrefix}-error`} className="text-[12px] font-bold text-[#ff603e]">{error}</span>}
      </div>
    </form>
  );
};

const ApproveDialog = ({ co, onClose, onDone, headers, userName }) => {
  const [f, setF] = useState({ saldo_inicial: "", transacciones: "", comisiones: "", retiro: "0", elaborado_por: userName || "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const final = Number(f.saldo_inicial || 0) + Number(f.transacciones || 0) - Number(f.comisiones || 0) - Number(f.retiro || 0) - Number(co.amount);
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const res = await axios.patch(`${API}/crm/cashouts/${co.id}/approve`, { ...f, saldo_inicial: Number(f.saldo_inicial), transacciones: Number(f.transacciones), comisiones: Number(f.comisiones), retiro: Number(f.retiro || 0) }, { headers });
      onDone(res.data);
    } catch (e) { setErr(e.response?.data?.detail || "Error al aprobar"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="cashout-approve-dialog" className="bg-white rounded-3xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-[16px] font-extrabold text-black">Aprobar cashout · {money(co.amount)}</h3>
        <p className="text-[12px] font-medium text-black/55 mt-1">{co.store} → {BANKS[co.bank]} · {co.account}. Completa los datos del recibo.</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          {[["saldo_inicial", "Saldo Inicial"], ["transacciones", "Transacciones"], ["comisiones", "Comisiones"], ["retiro", "Retiro"]].map(([k, l]) => (
            <Field key={k} label={l}><input data-testid={`approve-${k}`} type="number" step="0.01" min="0" className={inputCls} value={f[k]} onChange={set(k)} /></Field>
          ))}
          <div className="sm:col-span-2"><Field label="Elaborado Por"><input data-testid="approve-elaborado_por" className={inputCls} value={f.elaborado_por} onChange={set("elaborado_por")} /></Field></div>
        </div>
        <div className="mt-3 flex items-center justify-between bg-[#fafcf9] border border-black/[0.06] rounded-xl px-4 py-2.5 text-[13px]">
          <span className="font-semibold text-black/60">Cash Out {money(co.amount)} · Balance final</span><b data-testid="approve-balance" className={final < 0 ? "text-[#ff603e]" : "text-black"}>{money(final)}</b>
        </div>
        {err && <p className="mt-2 text-[12px] font-bold text-[#ff603e]">{err}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className={btnGhost}>Cancelar</button>
          <button data-testid="approve-confirm" onClick={submit} disabled={busy || f.saldo_inicial === "" || f.transacciones === "" || f.comisiones === "" || f.elaborado_por.length < 2} className={btnPrimary}>{busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Aprobar y generar recibo</button>
        </div>
      </div>
    </div>
  );
};

const AdminCashouts = ({ headers, user, apps }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [approving, setApproving] = useState(null);
  const [creating, setCreating] = useState(false);
  const [targetApp, setTargetApp] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const canApprove = ["superadmin", "admin"].includes(user.role);
  const [receiptNo, setReceiptNo] = useState(null);
  const [receiptInput, setReceiptInput] = useState("");
  const [receiptMsg, setReceiptMsg] = useState("");

  useEffect(() => {
    axios.get(`${API}/crm/cashouts/settings/receipt-number`, { headers }).then((r) => { setReceiptNo(r.data); setReceiptInput(String(r.data.next)); }).catch(() => {});
  }, [headers]);

  const saveReceiptNo = async () => {
    setReceiptMsg("");
    try {
      const r = await axios.put(`${API}/crm/cashouts/settings/receipt-number`, { next: Number(receiptInput) }, { headers });
      setReceiptNo(r.data); setReceiptMsg(`Próximo recibo: ${r.data.preview}`);
    } catch (e) { setReceiptMsg(e.response?.data?.detail || "No se pudo guardar"); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { setList((await axios.get(`${API}/crm/cashouts`, { headers })).data); } finally { setLoading(false); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const reject = async (co) => {
    const reason = window.prompt("Motivo del rechazo (se enviará al comercio):");
    if (reason === null) return;
    setBusy(co.id); setErr("");
    try {
      const res = await axios.patch(`${API}/crm/cashouts/${co.id}/reject`, { reason }, { headers });
      setList((l) => l.map((x) => (x.id === co.id ? res.data : x)));
    } catch (e) { setErr(e.response?.data?.detail || "Error al rechazar"); } finally { setBusy(""); }
  };

  const create = async (payload) => {
    if (!targetApp) { setErr("Selecciona el comercio"); return; }
    setBusy("create"); setErr("");
    try {
      const res = await axios.post(`${API}/crm/cashouts/${targetApp}`, payload, { headers });
      setList((l) => [res.data, ...l]); setCreating(false);
    } catch (e) { setErr(e.response?.data?.detail || "Error al crear"); } finally { setBusy(""); }
  };

  const shown = list.filter((c) => filter === "all" || c.status === filter);
  const counts = list.reduce((a, c) => ({ ...a, [c.status]: (a[c.status] || 0) + 1 }), {});

  return (
    <div data-testid="admin-cashouts" className="space-y-4">
      <div className="bg-white border border-black/[0.06] rounded-2xl p-4 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex items-center gap-2 text-[14px] font-extrabold text-black"><Landmark size={18} className="text-[#0fb013]" /> Solicitudes de Cashout <span className="text-[11px] font-bold text-black/40">({list.length})</span></div>
        <div className="flex flex-wrap gap-1.5 lg:ml-auto">
          {[["all", "Todas"], ["pendiente", "Pendientes"], ["aprobado", "Aprobados"], ["rechazado", "Rechazados"]].map(([k, l]) => (
            <button key={k} data-testid={`cashout-filter-${k}`} onClick={() => setFilter(k)} className={`text-[11px] font-bold px-3 py-1.5 rounded-full border-2 ${filter === k ? "bg-black text-white border-black" : "border-black/10 text-black/55"}`}>{l}{k !== "all" && counts[k] ? ` (${counts[k]})` : ""}</button>
          ))}
        </div>
        {canApprove && <button data-testid="cashout-new" onClick={() => setCreating((v) => !v)} className={btnPrimary}><Plus size={13} /> Nueva solicitud</button>}
      </div>

      {receiptNo && (
        <div data-testid="receipt-number-box" className="bg-white border border-black/[0.06] rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1 text-[12px] font-semibold text-black/60">Numeración de recibos · próximo: <b data-testid="receipt-number-preview" className="text-black">{receiptNo.preview}</b></div>
          {user.role === "superadmin" && (
            <div className="flex items-center gap-2">
              <input data-testid="receipt-number-input" type="number" min="1" max="99999" value={receiptInput} onChange={(e) => setReceiptInput(e.target.value)} className="w-28 px-3 py-2 text-[13px] font-bold text-black bg-[#fafcf9] border-2 border-black/10 rounded-xl focus:border-[#0fb013] focus:outline-none" />
              <button data-testid="receipt-number-save" onClick={saveReceiptNo} disabled={!receiptInput || Number(receiptInput) === receiptNo.next} className={btnGhost}>Fijar próximo número</button>
              {receiptMsg && <span data-testid="receipt-number-msg" className="text-[12px] font-bold text-[#0fb013]">{receiptMsg}</span>}
            </div>
          )}
        </div>
      )}

      {creating && (
        <div className="bg-white border border-black/[0.06] rounded-2xl p-5">
          <Field label="Comercio (solicitud aprobada)">
            <select data-testid="cashout-target-app" className={inputCls} value={targetApp} onChange={(e) => setTargetApp(e.target.value)}>
              <option value="">Selecciona…</option>
              {apps.map((a) => <option key={a.id} value={a.id}>{a.reference} · {a.business?.name} ({a.status})</option>)}
            </select>
          </Field>
          <div className="mt-4"><CashoutForm onSubmit={create} onCancel={() => setCreating(false)} submitting={busy === "create"} error={err} testPrefix="admin-co" /></div>
        </div>
      )}

      {err && !creating && <p className="text-[12px] font-bold text-[#ff603e]">{err}</p>}

      {loading ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#0fb013]" /></div> : shown.length === 0 ? (
        <div data-testid="cashouts-empty" className="bg-white border border-black/[0.06] rounded-2xl p-12 text-center text-[13px] font-bold text-black/45">Sin solicitudes de cashout</div>
      ) : (
        <div className="bg-white border border-black/[0.06] rounded-2xl overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead><tr className="text-left text-[10px] uppercase tracking-wider text-black/45 border-b border-black/[0.06]">
              {["Fecha", "Comercio / Tienda", "Beneficiario", "Banco · Cuenta", "ID TRX", "Monto", "Estado", "Acciones"].map((h) => <th key={h} className="px-4 py-3 font-bold">{h}</th>)}
            </tr></thead>
            <tbody>
              {shown.map((c) => (
                <tr key={c.id} data-testid={`cashout-row-${c.id}`} className="border-b border-black/[0.04] hover:bg-[#fafcf9]">
                  <td className="px-4 py-3 font-semibold whitespace-nowrap">{c.date}</td>
                  <td className="px-4 py-3"><b className="text-black">{c.store}</b><br /><span className="text-[#0fb013] font-bold text-[10px]">{c.app_reference}</span> · <span className="text-black/45">{c.client_id}</span></td>
                  <td className="px-4 py-3">{c.beneficiary}<br /><span className="text-black/45">{c.email}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap">{BANKS[c.bank]}<br /><span className="text-black/55">{c.account} · {c.account_type === "ahorro" ? "Ahorro" : "Corriente"}</span></td>
                  <td className="px-4 py-3 font-mono text-[11px]">{c.trx_id}</td>
                  <td className="px-4 py-3 font-extrabold text-[13px] whitespace-nowrap">{money(c.amount)}</td>
                  <td className="px-4 py-3"><CashoutBadge status={c.status} />{c.status === "rechazado" && c.reject_reason && <p className="mt-1 text-[10px] text-black/50 max-w-[180px]">{c.reject_reason}</p>}{c.receipt && <p className="mt-1 text-[10px] font-bold text-black/50">{c.receipt.trx_number}</p>}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {c.status === "pendiente" && canApprove && (
                        <>
                          <button data-testid={`cashout-approve-${c.id}`} onClick={() => setApproving(c)} className="inline-flex items-center gap-1 text-[11px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] px-3 py-1.5 rounded-full"><CheckCircle2 size={12} /> Aprobar</button>
                          <button data-testid={`cashout-reject-${c.id}`} onClick={() => reject(c)} disabled={busy === c.id} className="inline-flex items-center gap-1 text-[11px] font-bold text-white bg-[#ff603e] hover:bg-[#e04f2f] px-3 py-1.5 rounded-full"><XCircle size={12} /> Rechazar</button>
                        </>
                      )}
                      {c.status === "aprobado" && (
                        <button data-testid={`cashout-receipt-${c.id}`} onClick={() => downloadReceipt(`${API}/crm/cashouts/${c.id}/receipt.pdf`, headers, `recibo_${c.receipt?.trx_number}.pdf`)} className={btnGhost}><FileDown size={12} /> Recibo PDF</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {approving && <ApproveDialog co={approving} headers={headers} userName={user.name} onClose={() => setApproving(null)} onDone={(u) => { setList((l) => l.map((x) => (x.id === u.id ? u : x))); setApproving(null); }} />}
    </div>
  );
};

export default AdminCashouts;
